import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PaperPlaneRight, Image, X, ArrowLeft } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const BroadcastPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [messageImage, setMessageImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const checkPermission = async () => {
      if (!user) {
        navigate('/');
        return;
      }
      if (user.role === 'super_admin') return;
      try {
        const res = await axios.get(`${API}/api/auth/me`);
        const powers = res.data?.user?.settings_powers || user?.settings_powers || {};
        if (powers.broadcast === false || powers.broadcast === 'false') {
          toast.error("Access denied: Broadcast power is disabled by Admin");
          navigate('/handler-dashboard');
        }
      } catch (err) {
        console.error("Permission check failed in BroadcastPage:", err);
      }
    };
    checkPermission();
    fetchClients();
  }, [user, navigate]);

  const fetchClients = async () => {
    try {
      // Get all customers
      const response = await axios.get(`${API}/api/users/all-customers`);
      setClients(response.data || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.id));
    }
  };

  const toggleClient = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter(id => id !== clientId));
    } else {
      setSelectedClients([...selectedClients, clientId]);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMessageImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const sendBroadcast = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select at least one client');
      return;
    }

    if (!messageText && !messageImage) {
      toast.error('Please enter a message or select an image');
      return;
    }

    setLoading(true);

    try {
      let imageUrl = null;

      // Upload image if exists
      if (messageImage) {
        const formData = new FormData();
        formData.append('file', messageImage);
        formData.append('media_type', 'image');

        const uploadResponse = await axios.post(`${API}/api/upload/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        imageUrl = uploadResponse.data.url;
      }

      // Send broadcast
      await axios.post(`${API}/api/broadcast/send`, {
        sender_id: user.id,
        recipient_ids: selectedClients,
        text: messageText || null,
        image_url: imageUrl
      });

      toast.success(`Broadcast sent to ${selectedClients.length} client${selectedClients.length > 1 ? 's' : ''}!`);
      
      // Reset
      setMessageText('');
      setMessageImage(null);
      setImagePreview(null);
      setSelectedClients([]);

    } catch (error) {
      console.error('Broadcast failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send broadcast';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client =>
    client.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.phone?.includes(searchQuery)
  );

  return (
    <div className="h-screen bg-wa-hover overflow-y-auto" style={{ paddingBottom: '50px' }}>
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="bg-wa-panel rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(user?.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard')}
              className="hover:bg-wa-hover p-2 rounded-full transition-colors"
            >
              <ArrowLeft size={24} weight="bold" className="text-wa-text-primary" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-wa-text-primary">Broadcast Message</h1>
              <p className="text-wa-text-secondary mt-1">Send message to multiple clients at once</p>
            </div>
          </div>
        </div>

        {/* Client Selection */}
        <div className="bg-wa-panel rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Select Recipients</h2>
            <button
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-wa-text-primary"
            >
              {selectedClients.length === filteredClients.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg mb-4"
          />

          {/* Selected Count */}
          <div className="mb-4 text-sm text-wa-text-secondary">
            {selectedClients.length} client(s) selected
          </div>

          {/* Client List */}
          <div className="max-h-64 overflow-y-auto border rounded-lg">
            {filteredClients.map(client => (
              <label
                key={client.id}
                className="flex items-center p-3 hover:bg-wa-hover cursor-pointer border-b last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={selectedClients.includes(client.id)}
                  onChange={() => toggleClient(client.id)}
                  className="mr-3 w-4 h-4"
                />
                <div>
                  <div className="font-medium">{client.username}</div>
                  <div className="text-sm text-wa-text-secondary">{client.phone}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Message Compose */}
        <div className="bg-wa-panel rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Compose Message</h2>

          {/* Image Upload */}
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer text-blue-600 hover:text-wa-text-primary">
              <Image size={24} />
              <span>Add Image (Optional)</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </label>

            {imagePreview && (
              <div className="mt-4 relative inline-block">
                <img src={imagePreview} alt="Preview" className="max-w-xs rounded-lg shadow-md" />
                <button
                  onClick={() => {
                    setMessageImage(null);
                    setImagePreview(null);
                  }}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors"
                  title="Remove image"
                >
                  <X size={16} weight="bold" />
                </button>
                <div className="mt-2 text-xs text-wa-text-secondary italic">
                  ✓ Image added. You can also add a caption below.
                </div>
              </div>
            )}
          </div>

          {/* Text Message */}
          <div className="mb-1">
            <label className="text-sm font-medium text-wa-text-primary mb-2 block">
              {imagePreview ? 'Caption (Optional)' : 'Message Text'}
            </label>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder={imagePreview ? "Add a caption to your image..." : "Type your message here..."}
              className="w-full px-4 py-3 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="4"
            />
          </div>
          
          {/* Helper Text */}
          <div className="text-xs text-wa-text-secondary mb-4">
            {imagePreview && messageText ? (
              <span className="text-green-600 font-medium">✓ Image with caption will be sent</span>
            ) : imagePreview ? (
              <span>Image will be sent without caption</span>
            ) : messageText ? (
              <span>Text message will be sent</span>
            ) : (
              <span>Add image and/or text to send broadcast</span>
            )}
          </div>

          {/* Send Button */}
          <button
            onClick={sendBroadcast}
            disabled={loading || selectedClients.length === 0}
            className="mt-2 w-full bg-wa-primary-strong text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
          >
            <PaperPlaneRight size={20} weight="bold" />
            {loading ? 'Sending...' : `Send to ${selectedClients.length} Client(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BroadcastPage;
